import type {
  FetchOrgScheduleSearchArgs,
  FetchScheduleSearchArgs,
  ApiScheduleSearchRow,
} from './types/scheduleSearch.types';

import { get } from './axios';

interface ApiScheduleSearchResponse {
  data: ApiScheduleSearchRow[];
  total: number;
}

/**
 * The backend's `kinds` filter has no separate 'program' value — a `Schedule`
 * row's workout-vs-program split only exists in the RESPONSE `kind` field
 * (one query covers both, see `ScheduleSearchService.searchWorkouts`), not as
 * a filterable category. The page-level content-type filters this repo
 * already has (`ContentKind`/`EventKind`) DO treat them as separate options,
 * so a caller can pass `kinds: ['program']` alone (e.g. "workout" deselected)
 * — collapse that here rather than at every call site, or the backend's Zod
 * enum rejects the request outright.
 */
const toBackendKinds = (kinds?: string[]) =>
  kinds?.length
    ? Array.from(
        new Set(kinds.map((kind) => (kind === 'program' ? 'workout' : kind))),
      )
    : undefined;

export const fetchScheduleSearch = async ({
  kinds,
  limit = 20,
  page = 1,
  q,
}: FetchScheduleSearchArgs) => {
  const response = await get<ApiScheduleSearchResponse>('/v2/schedule-search', {
    query: {
      kinds: toBackendKinds(kinds),
      limit: String(limit),
      page: String(page),
      q,
    },
  });
  return {
    data: response?.data ?? [],
    total: response?.total ?? 0,
  };
};

export const fetchOrgScheduleSearch = async ({
  clientId,
  kinds,
  limit = 20,
  organizationId,
  page = 1,
  q,
  trainerId,
}: FetchOrgScheduleSearchArgs) => {
  const response = await get<ApiScheduleSearchResponse>(
    `/v2/organizations/${organizationId}/schedule-search`,
    {
      query: {
        clientId,
        kinds: toBackendKinds(kinds),
        limit: String(limit),
        page: String(page),
        q,
        trainerId,
      },
    },
  );
  return {
    data: response?.data ?? [],
    total: response?.total ?? 0,
  };
};
